import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";

interface StockImport {
  id: string;
  code: string;
  supplierName: string | null;
  status: string;
  totalCost: string;
  importedByEmail: string | null;
  createdAt: string;
  committedAt: string | null;
}

interface ImportsResponse {
  data: StockImport[];
  meta: { total: number };
}

const STATUS_LABELS: Record<string, string> = {
  draft: "Nháp",
  committed: "Đã nhập",
  cancelled: "Đã huỷ",
};

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-yellow-100 text-yellow-700",
  committed: "bg-green-100 text-green-700",
  cancelled: "bg-muted text-muted-foreground",
};

function fmtCost(cost: string) {
  const n = parseFloat(cost);
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(n);
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("vi-VN");
}

export function ImportListPage() {
  const { data: result, isLoading } = useQuery({
    queryKey: ["imports"],
    queryFn: () =>
      apiClient
        .get<ImportsResponse>("/v1/imports", { params: { limit: 50, offset: 0 } })
        .then((r) => r.data),
  });

  const imports: StockImport[] = result?.data ?? [];
  const total = result?.meta?.total ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Phiếu nhập hàng</h1>
        <Link to="/inventory/imports/new">
          <Button size="sm" className="gap-1.5">
            <Plus size={16} />
            Tạo phiếu
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Đang tải…</p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{total} phiếu</p>
          <Table>
            <THead>
              <Tr>
                <Th>Mã phiếu</Th>
                <Th>Nhà cung cấp</Th>
                <Th>Trạng thái</Th>
                <Th className="text-right">Tổng tiền</Th>
                <Th>Người tạo</Th>
                <Th>Ngày tạo</Th>
                <Th>Ngày nhập</Th>
              </Tr>
            </THead>
            <TBody>
              {imports.length === 0 && (
                <Tr>
                  <Td colSpan={7} className="text-center text-muted-foreground">
                    Chưa có phiếu nhập
                  </Td>
                </Tr>
              )}
              {imports.map((si) => (
                <Tr key={si.id}>
                  <Td>
                    <Link
                      to="/inventory/imports/$id"
                      params={{ id: si.id }}
                      className="font-mono text-sm font-medium hover:underline"
                    >
                      {si.code}
                    </Link>
                  </Td>
                  <Td>{si.supplierName ?? "—"}</Td>
                  <Td>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[si.status] ?? ""}`}>
                      {STATUS_LABELS[si.status] ?? si.status}
                    </span>
                  </Td>
                  <Td className="text-right">{fmtCost(si.totalCost)}</Td>
                  <Td className="text-xs text-muted-foreground">{si.importedByEmail ?? "—"}</Td>
                  <Td>{fmtDate(si.createdAt)}</Td>
                  <Td>{fmtDate(si.committedAt)}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </>
      )}
    </div>
  );
}
